import React from 'react';
import { useSearchParams } from 'react-router-dom';
import Agents from '../Agents';
import Tools from '../Tools';
import Models from '../Models';

/**
 * JourneysTab - Sub-tab wrapper for the Agents top-level tab:
 * Journeys (the Sankey) | Tools | Models.
 *
 * Mirrors TelemetryTab's sub-tab row (same pill markup, height, and vertical
 * rhythm) but carries no search bar — none of these sub-tabs are searchable.
 * Journeys is the default and renders the flow map; Tools/Models render their
 * entity tables as-is; their row drilldowns tag the Traces breadcrumb
 * (from=journeys-*) so the back-link points to the Agents tab.
 * (A future agents list would land here as the default "All agents" sub-tab.)
 */
const SUB_TABS = [
  { key: 'journeys', label: 'Business Tasks' },
  { key: 'tools',    label: 'Tools' },
  { key: 'models',   label: 'Models' },
];

const VALID_SUB_TABS = new Set(SUB_TABS.map((t) => t.key));

export default function JourneysTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawSubTab = searchParams.get('subtab');
  const activeSubTab = VALID_SUB_TABS.has(rawSubTab) ? rawSubTab : 'journeys';

  const handleSubTabChange = (key) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('subtab', key);
    setSearchParams(newParams);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Sub-tab row — same markup/classes/height as TelemetryTab's row */}
      <div className="flex items-center gap-3 mb-4 flex-shrink-0 flex-wrap">
        <div className="flex gap-2 flex-shrink-0">
          {SUB_TABS.map((tab) => {
            const isActive = tab.key === activeSubTab;
            return (
              <button
                key={tab.key}
                onClick={() => handleSubTabChange(tab.key)}
                className={`px-2.5 py-1 rounded-full text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-accent-primary-base text-text-inverse'
                    : 'bg-background-tertiary text-text-supporting hover:bg-background-quaternary'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sub-tab content */}
      <div className="flex-1 min-h-0">
        {activeSubTab === 'journeys' && <Agents embedded />}
        {activeSubTab === 'tools'    && <Tools />}
        {activeSubTab === 'models'   && <Models />}
      </div>
    </div>
  );
}
